// Cancellation Policy Utilities
// Handles all cancellation policy logic and calculations
// Supports stadium-specific policies for maximum flexibility

/**
 * POLICY TYPES:
 * - 'pay-at-venue': Traditional model - no upfront payment, free cancellation window
 * - 'full-payment': Full payment required upfront, refund based on cancellation window
 * 
 * Each stadium can configure:
 * - Policy type
 * - Cancellation window (hours)
 * - Refund percentage within window
 * - Refund percentage outside window
 */

/**
 * Default cancellation policies by type
 */
export const POLICY_TYPES = {
  PAY_AT_VENUE: 'pay-at-venue',
  FULL_PAYMENT: 'full-payment'
};

export const DEFAULT_POLICIES = {
  'pay-at-venue': {
    type: 'pay-at-venue',
    requiresPayment: false,
    cancellationWindow: 36, // hours
    refundPercentage: 100, // Not applicable (no payment)
    lateRefundPercentage: 0, // Fee applies if late
    lateCancellationFee: 50 // percentage
  },
  'full-payment': {
    type: 'full-payment',
    requiresPayment: true,
    cancellationWindow: 36, // hours
    refundPercentage: 100, // Full refund if cancelled in time
    lateRefundPercentage: 0 // No refund if late
  }
};

/**
 * Calculate hours between now and booking date/time
 * @param {string} bookingDate - Date in YYYY-MM-DD format
 * @param {string} bookingTime - Time in HH:MM format (start time)
 * @returns {number} Hours until booking (can be negative if past)
 */
export const calculateHoursUntilBooking = (bookingDate, bookingTime) => {
  try {
    // Create booking datetime
    const [year, month, day] = bookingDate.split('-').map(Number);
    const [hours, minutes] = bookingTime.split(':').map(Number);
    
    const bookingDateTime = new Date(year, month - 1, day, hours, minutes);
    const now = new Date();
    
    // Calculate difference in milliseconds, then convert to hours
    const diffMs = bookingDateTime - now;
    const diffHours = diffMs / (1000 * 60 * 60);
    
    return Math.round(diffHours * 10) / 10; // Round to 1 decimal
  } catch (error) {
    console.error('Error calculating hours until booking:', error);
    return 0;
  }
};

/**
 * Check if booking can be cancelled for free
 * Now supports stadium-specific policies
 * @param {string} bookingDate - Date in YYYY-MM-DD format
 * @param {string} bookingTime - Time in HH:MM format
 * @param {object} stadiumPolicy - Stadium's cancellation policy (optional)
 * @returns {boolean} True if can cancel for free
 */
export const canCancelForFree = (bookingDate, bookingTime, stadiumPolicy = null) => {
  const policy = stadiumPolicy || DEFAULT_POLICIES['pay-at-venue'];
  const hoursUntil = calculateHoursUntilBooking(bookingDate, bookingTime);
  return hoursUntil > policy.cancellationWindow;
};

/**
 * Get cancellation policy status with stadium-specific rules
 * @param {string} bookingDate - Date in YYYY-MM-DD format
 * @param {string} bookingTime - Time in HH:MM format
 * @param {object} stadiumPolicy - Stadium's cancellation policy (optional)
 * @returns {object} Policy status with details
 */
export const getCancellationPolicyStatus = (bookingDate, bookingTime, stadiumPolicy = null) => {
  const policy = stadiumPolicy || DEFAULT_POLICIES['pay-at-venue'];
  const hoursUntil = calculateHoursUntilBooking(bookingDate, bookingTime);
  
  if (hoursUntil < 0) {
    return {
      status: 'past',
      canCancel: false,
      isFree: false,
      message: 'Booking time has passed',
      hoursUntil: hoursUntil,
      policyType: policy.type
    };
  }
  
  // Within cancellation window
  if (hoursUntil > policy.cancellationWindow) {
    if (policy.type === 'full-payment') {
      return {
        status: 'full-refund',
        canCancel: true,
        isFree: true,
        message: 'Full refund available',
        hoursUntil: hoursUntil,
        refundPercentage: policy.refundPercentage,
        policyType: policy.type,
        requiresPayment: true
      };
    } else {
      return {
        status: 'free',
        canCancel: true,
        isFree: true,
        message: 'Free cancellation',
        hoursUntil: hoursUntil,
        feeAmount: 0,
        feePercentage: 0,
        policyType: policy.type,
        requiresPayment: false
      };
    }
  }
  
  // Outside cancellation window (late cancellation)
  if (policy.type === 'full-payment') {
    return {
      status: 'no-refund',
      canCancel: true, // Can cancel but no refund
      isFree: false,
      message: 'No refund - outside cancellation window',
      hoursUntil: hoursUntil,
      refundPercentage: policy.lateRefundPercentage,
      policyType: policy.type,
      requiresPayment: true
    };
  } else {
    return {
      status: 'fee-required',
      canCancel: false, // Cannot cancel in-app (must contact stadium)
      isFree: false,
      message: 'Cancellation fee required - contact stadium',
      hoursUntil: hoursUntil,
      feePercentage: policy.lateCancellationFee || 50,
      requiresContact: true,
      policyType: policy.type,
      requiresPayment: false
    };
  }
};

/**
 * Calculate cancellation fee (50% of booking price)
 * @param {number} bookingPrice - Original booking price
 * @returns {number} Cancellation fee amount
 */
export const calculateCancellationFee = (bookingPrice) => {
  return Math.round(bookingPrice * 0.5); // 50% fee
};

/**
 * Get formatted time remaining string
 * @param {number} hours - Hours until booking
 * @param {string} language - 'en' or 'ar'
 * @returns {string} Formatted string like "2 days, 5 hours" or "18 hours"
 */
export const formatTimeRemaining = (hours, language = 'en') => {
  if (hours < 0) {
    return language === 'ar' ? 'انتهى الوقت' : 'Past';
  }
  
  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return language === 'ar' 
      ? `${minutes} دقيقة`
      : `${minutes} minutes`;
  }
  
  if (hours < 24) {
    const roundedHours = Math.round(hours);
    return language === 'ar'
      ? `${roundedHours} ساعة`
      : `${roundedHours} hour${roundedHours !== 1 ? 's' : ''}`;
  }
  
  const days = Math.floor(hours / 24);
  const remainingHours = Math.round(hours % 24);
  
  if (language === 'ar') {
    if (remainingHours === 0) {
      return `${days} ${days === 1 ? 'يوم' : 'أيام'}`;
    }
    return `${days} ${days === 1 ? 'يوم' : 'أيام'} و ${remainingHours} ساعة`;
  }
  
  if (remainingHours === 0) {
    return `${days} day${days !== 1 ? 's' : ''}`;
  }
  return `${days} day${days !== 1 ? 's' : ''}, ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`;
};

/**
 * Get cancellation deadline based on stadium policy
 * @param {string} bookingDate - Date in YYYY-MM-DD format
 * @param {string} bookingTime - Time in HH:MM format
 * @param {object} stadiumPolicy - Stadium's cancellation policy (optional)
 * @returns {Date} Deadline for free cancellation
 */
export const getCancellationDeadline = (bookingDate, bookingTime, stadiumPolicy = null) => {
  const policy = stadiumPolicy || DEFAULT_POLICIES['pay-at-venue'];
  
  try {
    const [year, month, day] = bookingDate.split('-').map(Number);
    const [hours, minutes] = bookingTime.split(':').map(Number);
    
    const bookingDateTime = new Date(year, month - 1, day, hours, minutes);
    
    // Subtract cancellation window hours
    const deadline = new Date(bookingDateTime);
    deadline.setHours(deadline.getHours() - policy.cancellationWindow);
    
    return deadline;
  } catch (error) {
    console.error('Error calculating cancellation deadline:', error);
    return new Date();
  }
};

/**
 * Format deadline for display
 * @param {Date} deadline - Deadline date object
 * @param {string} language - 'en' or 'ar'
 * @returns {string} Formatted deadline string
 */
export const formatDeadline = (deadline, language = 'en') => {
  const options = { 
    weekday: 'short', 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  
  return language === 'ar'
    ? deadline.toLocaleDateString('ar-LB', options)
    : deadline.toLocaleDateString('en-US', options);
};

/**
 * Check if booking is in the past
 * @param {string} bookingDate - Date in YYYY-MM-DD format
 * @param {string} bookingTime - Time in HH:MM format
 * @returns {boolean} True if booking is in the past
 */
export const isBookingInPast = (bookingDate, bookingTime) => {
  const hoursUntil = calculateHoursUntilBooking(bookingDate, bookingTime);
  return hoursUntil < 0;
};

/**
 * Get policy badge info for UI with stadium-specific policies
 * @param {string} bookingDate - Date in YYYY-MM-DD format
 * @param {string} bookingTime - Time in HH:MM format
 * @param {number} bookingPrice - Original booking price
 * @param {string} language - 'en' or 'ar'
 * @param {object} stadiumPolicy - Stadium's cancellation policy (optional)
 * @returns {object} Badge styling and text
 */
export const getPolicyBadge = (bookingDate, bookingTime, bookingPrice, language = 'en', stadiumPolicy = null) => {
  const policy = getCancellationPolicyStatus(bookingDate, bookingTime, stadiumPolicy);
  const feeAmount = calculateCancellationFee(bookingPrice);
  
  if (policy.status === 'past') {
    return {
      text: language === 'ar' ? 'منتهي' : 'Past',
      color: 'gray',
      bgColor: 'bg-gray-100 dark:bg-gray-800',
      textColor: 'text-gray-600 dark:text-gray-400',
      borderColor: 'border-gray-300 dark:border-gray-700',
      icon: '⏱️',
      policyType: policy.policyType
    };
  }
  
  // Full payment policy - full refund available
  if (policy.status === 'full-refund') {
    return {
      text: language === 'ar' ? 'استرداد كامل متاح' : 'Full Refund Available',
      color: 'green',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      textColor: 'text-green-700 dark:text-green-400',
      borderColor: 'border-green-300 dark:border-green-700',
      icon: '✅',
      policyType: policy.policyType
    };
  }
  
  // Pay at venue - free cancellation
  if (policy.status === 'free') {
    return {
      text: language === 'ar' ? 'إلغاء مجاني' : 'Free Cancellation',
      color: 'green',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      textColor: 'text-green-700 dark:text-green-400',
      borderColor: 'border-green-300 dark:border-green-700',
      icon: '✅',
      policyType: policy.policyType
    };
  }
  
  // Full payment policy - no refund (late cancellation)
  if (policy.status === 'no-refund') {
    return {
      text: language === 'ar' ? 'لا يوجد استرداد' : 'No Refund',
      color: 'red',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      textColor: 'text-red-700 dark:text-red-400',
      borderColor: 'border-red-300 dark:border-red-700',
      icon: '❌',
      policyType: policy.policyType
    };
  }
  
  // Pay at venue - fee required (late cancellation)
  return {
    text: language === 'ar' 
      ? `رسوم إلغاء: $${feeAmount}` 
      : `Fee: $${feeAmount}`,
    color: 'orange',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    textColor: 'text-orange-700 dark:text-orange-400',
    borderColor: 'border-orange-300 dark:border-orange-700',
    icon: '⚠️',
    policyType: policy.policyType
  };
};

/**
 * Get human-readable policy description for display
 * @param {object} stadiumPolicy - Stadium's cancellation policy
 * @param {string} language - 'en' or 'ar'
 * @returns {string} Policy description
 */
export const getPolicyDescription = (stadiumPolicy, language = 'en') => {
  const policy = stadiumPolicy || DEFAULT_POLICIES['pay-at-venue'];
  
  if (policy.type === 'full-payment') {
    const hours = policy.cancellationWindow;
    if (language === 'ar') {
      return `دفع كامل مطلوب. استرداد كامل إذا تم الإلغاء قبل ${hours} ساعة.`;
    }
    return `Full payment required. Cancel before ${hours} hours for full refund.`;
  } else {
    const hours = policy.cancellationWindow;
    if (language === 'ar') {
      return `الدفع في الملعب. إلغاء مجاني قبل ${hours} ساعة.`;
    }
    return `Pay at venue. Free cancellation before ${hours} hours.`;
  }
};

// Export all functions
const cancellationPolicyUtils = {
  POLICY_TYPES,
  DEFAULT_POLICIES,
  calculateHoursUntilBooking,
  canCancelForFree,
  getCancellationPolicyStatus,
  calculateCancellationFee,
  formatTimeRemaining,
  getCancellationDeadline,
  formatDeadline,
  isBookingInPast,
  getPolicyBadge,
  getPolicyDescription
};

export default cancellationPolicyUtils;
